import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  type ActionHandler,
  Create,
  GenericField,
  InfoPanel,
  type ObjectSchemaDefinition,
  Paragraph,
  Section,
  SingleLineTextField,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import { Field } from 'formik';
import {
  UpdateIdpUserStoreDocument,
  type UpdateIdpUserStoreMutation,
  type UpdateIdpUserStoreMutationVariables,
} from 'generated/axauth-service';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import { environmentApolloClient } from 'modules/environmentApolloClient';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { getManagedServiceData } from 'util/managedServiceUtil';
import { validateUrl } from 'util/validationUtil';
import * as Yup from 'yup';
import { ensureError } from '../../../../../common/errors/ensure-error';
import type {
  AuthServiceDetails,
  SubmitResponse,
  WebHookFormData,
} from './UserSignUpWebHook.types';

const webhookValidation = Yup.object<ObjectSchemaDefinition>({
  userSignUpWebhook: Yup.string()
    .required('User Sign-Up webhook URL is required')
    .test(
      'user-sign-up-webhook-url',
      'User Sign-Up webhook must be a valid URL',
      validateUrl,
    ),
});

export const UserSignUpWebHookCreate: React.FC = () => {
  const { userStoreId, envId } = useParams<{
    userStoreId: string;
    envId: string;
  }>();
  const [authServiceConfig, setAuthServiceConfig] =
    useState<AuthServiceDetails>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const history = useHistory();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const serviceData = await getManagedServiceData(
          envId,
          ManagedServiceId.AXAUTH_SERVICE,
        );
        setAuthServiceConfig(serviceData);
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [envId, userStoreId]);

  const saveData = useCallback(
    async (formData: WebHookFormData): Promise<SubmitResponse> => {
      const envApolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
      );
      const response = await envApolloClient.mutate<
        UpdateIdpUserStoreMutation,
        UpdateIdpUserStoreMutationVariables
      >({
        mutation: UpdateIdpUserStoreDocument,
        variables: {
          input: {
            id: userStoreId,
            patch: { userSignUpWebhook: formData.userSignUpWebhook },
          },
        },
      });
      return response.data;
    },
    [envId, userStoreId],
  );

  const onProceed = useCallback<ActionHandler<WebHookFormData, SubmitResponse>>(
    ({ submitResponse }) => {
      if (submitResponse) {
        history.push(
          `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/userSignUp/details/secret/userSignUp`,
        );
      }
    },
    [history, envId, userStoreId],
  );

  const Panel: React.FC = () => {
    return (
      <InfoPanel>
        <Section>
          <Paragraph title="Tenant ID">{authServiceConfig?.tenantId}</Paragraph>
          <Paragraph title="Environment ID">
            {authServiceConfig?.environmentId}
          </Paragraph>
        </Section>
      </InfoPanel>
    );
  };

  const Form: React.FC = () => {
    return (
      <>
        <Field
          name="userSignUpWebhook"
          label="User Sign-Up Webhook URL"
          tooltipContent="A webhook that will receive the signUp-OTP and handles the user's sign-up process. It can be used to customize the user onboarding experience (i.e. send a verification email). After the user is verified, the [completeEndUserSignUp] mutation shall be called to complete the process."
          as={SingleLineTextField}
        />
        <GenericField name="secret" label="User Sign-Up Webhook Secret">
          <p>Not Generated</p>
        </GenericField>
      </>
    );
  };

  return (
    <>
      <Create<WebHookFormData, SubmitResponse>
        title="User Sign-Up Webhook"
        subtitle="Create User Sign-Up Webhook"
        validationSchema={webhookValidation}
        initialData={{
          loading,
          error,
        }}
        saveData={saveData}
        infoPanel={<Panel />}
        onProceed={onProceed}
      >
        <Form />
      </Create>
    </>
  );
};

export const UserSignUpWebHookCreateBreadCrumb: BreadcrumbResolver = () =>
  'New User Sign-Up Webhook';
